import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusPill } from '@/components/shared/StatusPill';
import { isTerminalSyncJobStatus, type SyncJob, type SyncJobStatus } from '@/lib/api/sync-jobs';

/**
 * Presentational progress modal for an in-flight (or just-finished)
 * sync job, per [ADR 0019](../../../../docs/adr/0019-async-merchant-sync-jobs.md).
 *
 * The polling + orders-cache invalidation live in the parent
 * (OrdersPage) per [ADR 0021](../../../../docs/adr/0021-frontend-feedback-loop-polish.md)
 * — that way closing this modal mid-sync doesn't stop the polling,
 * and the orders table still refreshes when the worker finishes.
 * This component just renders whatever job state it's given.
 */
interface SyncJobModalProps {
  readonly open: boolean;
  readonly job: SyncJob | undefined;
  readonly onClose: () => void;
}

export function SyncJobModal({ open, job, onClose }: SyncJobModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Syncing orders from BigCommerce</DialogTitle>
          <DialogDescription>
            You can close this — the sync continues in the background. Re-opening this page picks up
            the latest progress automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">{job ? <ProgressBody job={job} /> : <StartingBody />}</div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StartingBody() {
  return (
    <div className="text-muted-foreground flex items-center gap-2 text-sm">
      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
      <span>Starting…</span>
    </div>
  );
}

function ProgressBody({ job }: { job: SyncJob }) {
  const inFlight = !isTerminalSyncJobStatus(job.status);
  const synced = job.synced_count ?? 0;
  const enqueueing = !job.enqueue_completed_at && inFlight;
  // Spin the "Orders queued" count only while we're still listing BC.
  // Spin "Orders synced" while the worker is still draining the queue.
  const queuedSpin = enqueueing;
  const syncedSpin = inFlight && !!job.enqueue_completed_at && synced < job.enqueued_count;
  return (
    <>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">Status</span>
        {renderStatusPill(job.status)}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">Orders queued</span>
        <span className="text-foreground flex items-center gap-2 text-sm font-medium tabular-nums">
          {queuedSpin ? (
            <Loader2 className="text-muted-foreground size-3.5 animate-spin" aria-hidden="true" />
          ) : null}
          {job.enqueued_count}
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm">Orders synced</span>
        <span className="text-foreground flex items-center gap-2 text-sm font-medium tabular-nums">
          {syncedSpin ? (
            <Loader2 className="text-muted-foreground size-3.5 animate-spin" aria-hidden="true" />
          ) : null}
          {synced}
        </span>
      </div>
      {job.status === 'completed' && job.truncated ? (
        <p className="text-muted-foreground text-xs">
          More may be available — press Sync now again to continue.
        </p>
      ) : null}
      {job.status === 'failed' && job.error ? (
        <p className="text-destructive text-sm">{job.error}</p>
      ) : null}
    </>
  );
}

function renderStatusPill(status: SyncJobStatus) {
  if (status === 'completed') return <StatusPill tone="success">Completed</StatusPill>;
  if (status === 'failed') return <StatusPill tone="muted">Failed</StatusPill>;
  if (status === 'running') return <StatusPill tone="warning">Running</StatusPill>;
  return <StatusPill tone="muted">Queued</StatusPill>;
}
